import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { AppShell, PageHeader } from '@/components/AppShell';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Heart,
  MessageCircle,
  UserPlus,
  Send,
  Share2,
  Loader2,
  Activity as ActivityIcon,
  PanelLeftOpen,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  HelpCircle,
} from 'lucide-react';
import {
  listActivities,
  getActivityStats,
  listActivityDevices,
  type AgentActivity,
  type VerificationStatus,
  type MentionState,
} from '@/services/vmos-activities-api';
import { listBrands } from '@/services/brands-api';

const PLATFORM_OPTIONS = [
  { value: 'all', label: 'All platforms' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'reddit', label: 'Reddit' },
] as const;

const ACTIVITY_TYPE_OPTIONS = [
  { value: 'all', label: 'All activities' },
  { value: 'like', label: 'Likes' },
  { value: 'comment', label: 'Comments' },
  { value: 'follow', label: 'Follows' },
  { value: 'post', label: 'Posts' },
  { value: 'share', label: 'Shares' },
] as const;

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
] as const;

const VERIFICATION_OPTIONS: Array<{ value: 'all' | VerificationStatus; label: string }> = [
  { value: 'all', label: 'Any verification' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified_present', label: 'Verified present' },
  { value: 'verified_absent', label: 'Verified absent' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'skipped', label: 'Skipped' },
];

const MENTION_OPTIONS: Array<{ value: MentionState; label: string }> = [
  { value: 'any', label: 'Any mention state' },
  { value: 'matched', label: 'Mentioned brand' },
  { value: 'unmatched', label: 'Did not mention brand' },
];

type DateRange = (typeof DATE_RANGE_OPTIONS)[number]['value'];

function dateFromForRange(range: DateRange): string | undefined {
  const now = new Date();
  if (range === 'today') {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  if (range === '7d') return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
  if (range === '30d') return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
  return undefined;
}

const ACTIVITY_ICON: Record<string, typeof Heart> = {
  like: Heart,
  comment: MessageCircle,
  follow: UserPlus,
  post: Send,
  share: Share2,
};

const ACTIVITY_COLOR: Record<string, string> = {
  like: 'text-pink-500',
  comment: 'text-blue-500',
  follow: 'text-green-500',
  post: 'text-purple-500',
  share: 'text-amber-500',
};

const PLATFORM_LABEL: Record<string, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
  youtube: 'YouTube',
  reddit: 'Reddit',
};

const PAGE_SIZE = 50;
const POLL_MS = 10_000;

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function StatCell({ icon: Icon, color, label, value }: { icon: typeof Heart; color: string; label: string; value: number }) {
  return (
    <div className="flex flex-col gap-0.5 px-4 py-3 border-r last:border-r-0 min-w-0">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className={`h-3.5 w-3.5 ${color}`} />
        <span className="truncate">{label}</span>
      </div>
      <span className="text-xl font-semibold font-mono tabular-nums">{value.toLocaleString()}</span>
    </div>
  );
}

function VerificationBadge({ status }: { status: VerificationStatus }) {
  const config: Record<VerificationStatus, { icon: typeof CheckCircle2; label: string; cls: string }> = {
    pending: { icon: Loader2, label: 'pending', cls: 'text-muted-foreground' },
    verified_present: { icon: CheckCircle2, label: 'present', cls: 'text-green-600 border-green-600/40' },
    verified_absent: { icon: XCircle, label: 'absent', cls: 'text-destructive border-destructive/40' },
    unknown: { icon: HelpCircle, label: 'unknown', cls: 'text-amber-600 border-amber-600/40' },
    skipped: { icon: HelpCircle, label: '—', cls: 'text-muted-foreground' },
  };
  const { icon: Icon, label, cls } = config[status];
  if (status === 'skipped') {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <Badge variant="outline" className={`text-xs ${cls}`}>
      <Icon className={`h-3 w-3 mr-1 ${status === 'pending' ? 'animate-spin' : ''}`} />
      {label}
    </Badge>
  );
}

function ActivityRow({
  row,
  deviceLabel,
  brandLabel,
  expanded,
  onToggleExpanded,
}: {
  row: AgentActivity;
  deviceLabel: string;
  brandLabel: string | null;
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  const Icon = ACTIVITY_ICON[row.activity_type] ?? ActivityIcon;
  const color = ACTIVITY_COLOR[row.activity_type] ?? 'text-muted-foreground';
  const preview = row.content_text ? row.content_text.slice(0, 80) + (row.content_text.length > 80 ? '…' : '') : '—';
  const target = row.target_username || row.target_content_url || '—';
  const isComment = row.activity_type === 'comment';
  const drift = isComment && row.typed_text != null && row.content_text != null && row.typed_text !== row.content_text;

  return (
    <Fragment>
      <TableRow className={isComment ? 'cursor-pointer' : ''} onClick={isComment ? onToggleExpanded : undefined}>
        <TableCell className="w-6 p-0 pl-2">
          {isComment ? (
            expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ) : null}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
          {formatTimestamp(row.created_at)}
        </TableCell>
        <TableCell className="font-mono text-xs whitespace-nowrap" title={row.pad_id}>
          {deviceLabel}
        </TableCell>
        <TableCell>
          <Badge variant="secondary">{PLATFORM_LABEL[row.platform] ?? row.platform}</Badge>
        </TableCell>
        <TableCell>
          <div className={`flex items-center gap-1.5 ${color}`}>
            <Icon className="h-4 w-4" />
            <span className="capitalize text-sm">{row.activity_type}</span>
          </div>
        </TableCell>
        <TableCell className="text-xs">
          {brandLabel ? (
            <Badge variant="outline" className="font-normal">{brandLabel}</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-xs">
          {!row.brand_id ? (
            <span className="text-muted-foreground">—</span>
          ) : row.brand_alias_matched ? (
            <Badge variant="outline" className="text-green-600 border-green-600/40" title={row.brand_alias_matched}>
              ✓
            </Badge>
          ) : isComment ? (
            <Badge variant="outline" className="text-amber-600 border-amber-600/40" title="Comment posted under this brand but no alias was found in the text">
              missing
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell>
          <VerificationBadge status={row.verification_status} />
        </TableCell>
        <TableCell className="max-w-[180px] truncate text-sm" title={target}>
          {target}
        </TableCell>
        <TableCell className="max-w-[280px] truncate text-sm" title={row.content_text || ''}>
          <div className="flex items-center gap-1">
            {drift && <AlertTriangle className="h-3 w-3 text-amber-600 shrink-0" />}
            <span className="truncate">{preview}</span>
          </div>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">{row.step_number}</TableCell>
        <TableCell>
          {row.result === 'success' ? (
            <Badge variant="outline" className="text-green-600 border-green-600/40">success</Badge>
          ) : (
            <Badge variant="destructive">{row.result}</Badge>
          )}
        </TableCell>
      </TableRow>
      {expanded && isComment && (
        <TableRow className="bg-muted/30">
          <TableCell colSpan={11} className="text-xs">
            <div className="grid gap-3 p-2 md:grid-cols-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">LLM-claimed content_text</div>
                <div className="font-mono whitespace-pre-wrap break-words bg-background border rounded p-2">
                  {row.content_text || <span className="text-muted-foreground italic">(empty)</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Actual typed_text (keystrokes)</div>
                <div className={`font-mono whitespace-pre-wrap break-words border rounded p-2 ${drift ? 'bg-amber-500/10 border-amber-500/40' : 'bg-background'}`}>
                  {row.typed_text || <span className="text-muted-foreground italic">(not captured)</span>}
                </div>
              </div>
              {row.verification_notes && (
                <div className="md:col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Verification notes</div>
                  <div className="font-mono whitespace-pre-wrap break-words bg-background border rounded p-2">
                    {row.verification_notes}
                  </div>
                </div>
              )}
              {drift && (
                <div className="md:col-span-2 text-amber-700 dark:text-amber-500 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  LLM-claimed content_text differs from actual keystrokes — possible LLM fabrication.
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

export default function AgentActivityPage() {
  const [platform, setPlatform] = useState<string>('all');
  const [activityType, setActivityType] = useState<string>('all');
  const [padId, setPadId] = useState<string>('all');
  const [brandId, setBrandId] = useState<string>('all');         // 'all' | 'none' | <uuid>
  const [verificationStatus, setVerificationStatus] = useState<'all' | VerificationStatus>('all');
  const [mentionState, setMentionState] = useState<MentionState>('any');
  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [offset, setOffset] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const dateFrom = useMemo(() => dateFromForRange(dateRange), [dateRange]);

  const filters = useMemo(
    () => ({
      platform: platform === 'all' ? undefined : platform,
      activity_type: activityType === 'all' ? undefined : activityType,
      pad_id: padId === 'all' ? undefined : padId,
      brand_id: brandId === 'all' ? undefined : brandId,
      verification_status: verificationStatus === 'all' ? undefined : verificationStatus,
      mention_state: mentionState === 'any' ? undefined : mentionState,
      date_from: dateFrom,
    }),
    [platform, activityType, padId, brandId, verificationStatus, mentionState, dateFrom],
  );

  const { data: devicesData } = useQuery({
    queryKey: ['vmos-activity-devices'],
    queryFn: listActivityDevices,
    staleTime: 60_000,
  });

  const { data: brands = [] } = useQuery({
    queryKey: ['brands'],
    queryFn: listBrands,
    staleTime: 60_000,
  });

  const deviceLabelByPad = useMemo(() => {
    const map: Record<string, string> = {};
    devicesData?.devices.forEach((d) => {
      map[d.pad_id] = d.device_name || d.pad_id;
    });
    return map;
  }, [devicesData]);

  const brandLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    brands.forEach((b) => {
      map[b.id] = b.name;
    });
    return map;
  }, [brands]);

  const isLiveWindow = dateRange === 'today';

  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['vmos-activity-stats', filters],
    queryFn: () => getActivityStats(filters),
    refetchInterval: isLiveWindow ? POLL_MS : false,
  });

  const {
    data: list,
    isLoading: listLoading,
    refetch: refetchList,
  } = useQuery({
    queryKey: ['vmos-activity-list', filters, offset],
    queryFn: () => listActivities({ ...filters, limit: PAGE_SIZE, offset }),
    refetchInterval: isLiveWindow ? POLL_MS : false,
  });

  const total = list?.total ?? 0;
  const rows = list?.activities ?? [];
  const fpSignals = stats?.false_positive_signals;

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleResetFilters = () => {
    setPlatform('all');
    setActivityType('all');
    setPadId('all');
    setBrandId('all');
    setVerificationStatus('all');
    setMentionState('any');
    setDateRange('7d');
    setOffset(0);
  };

  const handleRefresh = () => {
    refetchStats();
    refetchList();
  };

  return (
    <AppShell>
      <div className="flex flex-col h-full">
        <PageHeader>
            <SidebarTrigger>
              <PanelLeftOpen className="h-4 w-4" />
            </SidebarTrigger>
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold leading-tight">Activity</h1>
              <p className="text-xs text-muted-foreground">
                Verified actions across all runs.
              </p>
            </div>
            <div className="ml-auto">
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
        </PageHeader>

          <div className="flex-1 overflow-auto px-6 py-6 space-y-5">
            {/* Filters row */}
            <div className="flex flex-wrap items-end gap-2">
              <FilterField label="Date range">
                <Select value={dateRange} onValueChange={(v) => { setDateRange(v as DateRange); setOffset(0); }}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{DATE_RANGE_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Platform">
                <Select value={platform} onValueChange={(v) => { setPlatform(v); setOffset(0); }}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLATFORM_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Activity">
                <Select value={activityType} onValueChange={(v) => { setActivityType(v); setOffset(0); }}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIVITY_TYPE_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Device">
                <Select value={padId} onValueChange={(v) => { setPadId(v); setOffset(0); }}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All devices</SelectItem>
                    {devicesData?.devices.map((d) => <SelectItem key={d.pad_id} value={d.pad_id}>{d.device_name || d.pad_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Brand">
                <Select value={brandId} onValueChange={(v) => { setBrandId(v); setOffset(0); }}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All brands</SelectItem>
                    <SelectItem value="none">No brand</SelectItem>
                    {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Verification">
                <Select value={verificationStatus} onValueChange={(v) => { setVerificationStatus(v as 'all' | VerificationStatus); setOffset(0); }}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{VERIFICATION_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Mention">
                <Select value={mentionState} onValueChange={(v) => { setMentionState(v as MentionState); setOffset(0); }}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{MENTION_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                </Select>
              </FilterField>
              <Button variant="ghost" size="sm" onClick={handleResetFilters} className="h-8 text-xs">
                Reset
              </Button>
            </div>

            {/* Stats strip */}
            <div className="border rounded-lg overflow-hidden">
              <div className="grid grid-cols-3 sm:grid-cols-6 divide-x">
                <StatCell icon={ActivityIcon} color="text-primary" label="Total" value={stats?.total ?? 0} />
                <StatCell icon={Heart} color={ACTIVITY_COLOR.like} label="Likes" value={stats?.by_activity_type?.like ?? 0} />
                <StatCell icon={MessageCircle} color={ACTIVITY_COLOR.comment} label="Comments" value={stats?.by_activity_type?.comment ?? 0} />
                <StatCell icon={UserPlus} color={ACTIVITY_COLOR.follow} label="Follows" value={stats?.by_activity_type?.follow ?? 0} />
                <StatCell icon={Send} color={ACTIVITY_COLOR.post} label="Posts" value={stats?.by_activity_type?.post ?? 0} />
                <StatCell icon={Share2} color={ACTIVITY_COLOR.share} label="Shares" value={stats?.by_activity_type?.share ?? 0} />
              </div>
            </div>

            {/* False-positive warnings */}
            {fpSignals && (fpSignals.verified_absent_count > 0 || fpSignals.branded_comments_unmatched > 0) && (
              <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border border-amber-500/40 bg-amber-500/5 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                <div className="flex-1 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  {fpSignals.verified_absent_count > 0 && (
                    <button
                      className="underline-offset-2 hover:underline text-amber-700 dark:text-amber-500"
                      onClick={() => { setVerificationStatus('verified_absent'); setOffset(0); }}
                    >
                      <span className="font-mono tabular-nums">{fpSignals.verified_absent_count.toLocaleString()}</span> comment{fpSignals.verified_absent_count === 1 ? '' : 's'} verified absent on platform
                    </button>
                  )}
                  {fpSignals.branded_comments_unmatched > 0 && (
                    <button
                      className="underline-offset-2 hover:underline text-amber-700 dark:text-amber-500"
                      onClick={() => { setActivityType('comment'); setMentionState('unmatched'); setOffset(0); }}
                    >
                      <span className="font-mono tabular-nums">{fpSignals.branded_comments_unmatched.toLocaleString()}</span> branded comment{fpSignals.branded_comments_unmatched === 1 ? '' : 's'} did not mention the brand ({fpSignals.branded_comments_total} total branded)
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Feed */}
            <div className="border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Activity feed</span>
                  {(statsLoading || listLoading) && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                  {isLiveWindow && (
                    <span className="flex items-center gap-1.5 text-xs text-green-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                      live
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground font-mono tabular-nums">{total.toLocaleString()} total</span>
              </div>

              <div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-6"></TableHead>
                      <TableHead>When</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead>Activity</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Mentioned?</TableHead>
                      <TableHead>Verified?</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Content preview</TableHead>
                      <TableHead>Step</TableHead>
                      <TableHead>Result</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 && !listLoading && (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center text-muted-foreground py-12">
                          No activities match these filters yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {rows.map((r) => (
                      <ActivityRow
                        key={r.id}
                        row={r}
                        deviceLabel={deviceLabelByPad[r.pad_id] || r.pad_id}
                        brandLabel={r.brand_id ? (brandLabelById[r.brand_id] || 'Unknown brand') : null}
                        expanded={expandedIds.has(r.id)}
                        onToggleExpanded={() => toggleExpanded(r.id)}
                      />
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {total > PAGE_SIZE && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <span className="text-xs text-muted-foreground">
                      {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total.toLocaleString()}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={offset + PAGE_SIZE >= total}
                        onClick={() => setOffset(offset + PAGE_SIZE)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
    </AppShell>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 min-w-[120px]">
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}
